import { ChartType } from '@trlogic/ui-pack';
import { CartesianTickOptions, ChartOptions, CoreInteractionOptions, LegendOptions, TitleOptions, TooltipItem, TooltipOptions } from 'chart.js';
import { ChartEditorOptions, ChartJsDataModel, MetricItem } from '../domain/chart/ChartDomains';

class ChartUtil {
  private static readonly colors = ['#84C9B3', '#84A8D5', '#C56565', '#A488CB', '#DBA9C7', '#EDD981', '#CDC1A2', '#E9A669', '#B98171', '#EE8769'];

  public static getHorizontalRowedOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      indexAxis: 'y' as const,
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('y'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('y', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        x: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
        y: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
      elements: {
        bar: {
          borderWidth: 2,
        },
      },
    };
  }

  public static getHorizontalStackedOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      indexAxis: 'y' as const,
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('y'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('y', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
        y: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    };
  }

  public static getVerticalStackedOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('x'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('x', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
        y: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    };
  }

  public static getVerticalRowedOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('x'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('x', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        x: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
        y: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    };
  }

  public static getDonutOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      plugins: {
        title: this.getTitleOptions(settings),
        legend: {
          ...this.getLegendOptions(settings.legendDisplay as boolean),
          labels: {
            boxWidth: 10,
            font: {
              size: 10,
            },
          },
        },
      },
    };
  }

  public static getPieOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      plugins: {
        title: {
          ...this.getTitleOptions(settings),
        },
        legend: {
          ...this.getLegendOptions(settings.legendDisplay as boolean),
          maxWidth: 10,
          labels: {
            boxWidth: 10,
            font: {
              size: 10,
            },
          },
        },
      },
      layout: {},
      elements: {
        arc: {
          borderWidth: 0,
        },
      },
    };
  }

  public static getLineOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('x'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('x', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        x: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
        y: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    } as ChartOptions;
  }

  public static getLineStackedOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('x'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('x', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        y: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
        x: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    } as ChartOptions;
  }

  public static getAreaStackedOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('x'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('x', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        y: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
        x: {
          stacked: true,
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    };
  }

  public static getAreaOptions(settings: Partial<ChartUtilSettings>): ChartOptions {
    return {
      ...this.getDefaultOptions(),
      interaction: {
        ...this.getInteractionOptions('x'),
      },
      plugins: {
        legend: {
          ...this.getLegendOptions(settings.legendDisplay || true),
        },
        title: {
          ...this.getTitleOptions(settings),
        },
        tooltip: {
          ...this.getTooltipOptions('x', settings.metrics as MetricItem[]),
        },
      },
      scales: {
        y: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
        x: {
          ticks: {
            ...this.getTickOptions(),
          },
        },
      },
    };
  }

  private static getDefaultOptions(): Partial<ChartOptions> {
    return {
      responsive: true,
      aspectRatio: 1,
      maintainAspectRatio: false,
    };
  }

  private static getTitleOptions(settings: Partial<ChartUtilSettings>): Partial<TitleOptions> {
    return {
      display: false,
      text: settings.name,
    };
  }

  private static getLegendOptions(legendDisplay: boolean): Partial<LegendOptions<any>> {
    return {
      display: !legendDisplay,
      position: 'top' as const,
    };
  }

  private static getTooltipOptions(axis: 'x' | 'y', metrics?: MetricItem[]): Partial<TooltipOptions> {
    return {
      callbacks: {
        label: function (context: TooltipItem<any>) {
          const metric = metrics?.find((m) => m.metric.appearance.name == context.dataset.label);
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          label += (metric?.metric.appearance.prefix || '') + context.raw + (metric?.metric.appearance.suffix || '');
          return label;
        },
      } as any,
      axis: axis,
      intersect: false,
      enabled: true,
      mode: 'index',
    };
  }

  private static getInteractionOptions(axis: 'x' | 'y'): Partial<CoreInteractionOptions> {
    return {
      mode: 'index',
      intersect: false,
      axis: axis,
    };
  }

  private static getTickOptions(): Partial<CartesianTickOptions> {
    return {
      maxRotation: 0,
      minRotation: 0,
      autoSkip: true,
      callback: function (tickValue, index, ticks) {
        const characterLimit = 12;
        const label = this.getLabelForValue(tickValue as any);
        if (label?.length >= characterLimit) {
          return (
            label
              ?.slice(0, label.length)
              .substring(0, characterLimit - 1)
              .trim() + '...'
          );
        }
        return label;
      },
    };
  }

  public static getColors = (): string[] => {
    return this.colors;
  };

  public static setColorPalette = (chartOptions: ChartEditorOptions, response: ChartJsDataModel, chartType: ChartType): ChartJsDataModel => {
    if (response == null) return response;

    response.datasets = response?.datasets?.map((dataset) => {
      const metricItem = chartOptions.metricItem?.find((item: MetricItem) => item.metric.appearance.name == dataset.label);

      if (!metricItem) return dataset;

      switch (chartType) {
        case ChartType.DONUT:
        case ChartType.PIE:
          dataset.backgroundColor = [
            this.colors[0],
            this.colors[1],
            this.colors[2],
            this.colors[3],
            this.colors[4],
            this.colors[5],
            this.colors[6],
            this.colors[7],
            this.colors[8],
            this.colors[9],
          ];
          dataset.borderColor = 'white';
          break;
        case ChartType.AREA_STACKED:
        case ChartType.AREA:
        case ChartType.LINE_STACKED:
          dataset.backgroundColor = metricItem.metric.appearance.colorSettings.activeColor;
          dataset.borderColor = metricItem.metric.appearance.colorSettings.activeColor;
          (dataset as any).fill = true;
          (dataset as any).pointRadius = 0;
          (dataset as any).pointHoverRadius = 0;
          break;
        case ChartType.LINE:
          dataset.backgroundColor = metricItem.metric.appearance.colorSettings.activeColor;
          dataset.borderColor = metricItem.metric.appearance.colorSettings.activeColor;
          (dataset as any).fill = false;
          break;
        default:
          dataset.backgroundColor = metricItem.metric.appearance.colorSettings.activeColor;
          dataset.borderColor = metricItem.metric.appearance.colorSettings.activeColor;
          (dataset as any).fill = false;
      }

      (dataset as any).tension = 0;
      return dataset;
    });
    return response;
  };

  public static getChartOptionsByChartType(chartType: ChartType, chartSettings: Partial<ChartUtilSettings>) {
    switch (chartType) {
      case ChartType.BAR_VERTICAL_ROWED:
        return this.getVerticalRowedOptions(chartSettings);
      case ChartType.BAR_VERTICAL_ROWED_STACKED:
      case ChartType.BAR_VERTICAL_STACKED:
        return this.getVerticalStackedOptions(chartSettings);
      case ChartType.BAR_HORIZONTAL_ROWED:
        return this.getHorizontalRowedOptions(chartSettings);
      case ChartType.BAR_HORIZONTAL_ROWED_STACKED:
      case ChartType.BAR_HORIZONTAL_STACKED:
        return this.getHorizontalStackedOptions(chartSettings);
      case ChartType.DONUT:
        return this.getDonutOptions(chartSettings);
      case ChartType.PIE:
        return this.getPieOptions(chartSettings);
      case ChartType.LINE_STACKED:
        return this.getLineStackedOptions(chartSettings);
      case ChartType.LINE:
        return this.getLineOptions(chartSettings);
      case ChartType.AREA:
        return this.getAreaOptions(chartSettings);
      case ChartType.AREA_STACKED:
        return this.getAreaStackedOptions(chartSettings);
      default:
        return this.getVerticalRowedOptions(chartSettings);
    }
  }
}

export default ChartUtil;

export interface ChartUtilSettings {
  name: string;
  legendDisplay: boolean;
  metrics: MetricItem[];
}
